import React, { useState, useRef, useMemo } from 'react';
import { useDriverApp } from './DriverAppProvider';

const ALL_DOC_TYPES = [
  { value: 'drivers_license', label: "Driver's License", icon: '🪪' },
  { value: 'medical_card', label: 'Medical Card', icon: '🏥' },
  { value: 'hazmat_cert', label: 'Hazmat Certificate', icon: '☢️' },
  { value: 'twic_card', label: 'TWIC Card', icon: '🔒' },
  { value: 'proof_of_insurance', label: 'Proof of Insurance', icon: '📋' },
  { value: 'vehicle_registration', label: 'Vehicle Registration', icon: '🚗' },
  { value: 'background_check', label: 'Background Check Consent', icon: '✅' },
  { value: 'mvr_report', label: 'MVR Report', icon: '🛣️' },
  { value: 'other', label: 'Other Document', icon: '📄' },
];

const LicenseScannerScreen = ({ onComplete }) => {
  const { theme, updateUser, markProfileComplete, requiredDocuments, user } = useDriverApp();

  const isDark = theme === 'dark';
  const bg = isDark ? 'bg-black' : 'bg-white';
  const text = isDark ? 'text-white' : 'text-black';
  const subtext = isDark ? 'text-white/60' : 'text-black/60';
  const border = isDark ? 'border-[#262626]' : 'border-[#e5e5e5]';
  const card = isDark ? 'bg-[#0a0a0a] border-[#262626]' : 'bg-[#f5f5f5] border-[#e5e5e5]';

  // If admin specified required docs use those, otherwise show full list
  const hasRequiredDocs = requiredDocuments?.length > 0;
  const docList = hasRequiredDocs
    ? ALL_DOC_TYPES.filter(d => requiredDocuments.includes(d.value))
    : ALL_DOC_TYPES.slice(0, 4); // default: DL, medical, hazmat, TWIC

  // Uploaded docs state (persisted to localStorage)
  const [uploadedDocs, setUploadedDocs] = useState(() => {
    if (!user?.id) return [];
    try { return JSON.parse(localStorage.getItem(`driver_docs_${user.id}`) || '[]'); }
    catch { return []; }
  });

  const uploadedTypes = useMemo(() => uploadedDocs.map(d => d.type), [uploadedDocs]);
  const requiredUploaded = hasRequiredDocs
    ? requiredDocuments.filter(r => uploadedTypes.includes(r)).length
    : uploadedTypes.length;
  const totalRequired = hasRequiredDocs ? requiredDocuments.length : docList.length;
  const allDone = hasRequiredDocs && requiredUploaded >= totalRequired;

  const [step, setStep] = useState('list'); // list | scan | preview | uploading
  const [currentDocType, setCurrentDocType] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [selectedFile, setSelectedFile] = useState(null);
  const fileInputRef = useRef(null);

  const startScan = (docType) => {
    setCurrentDocType(docType);
    setStep('scan');
  };

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setSelectedFile(file);
    setPreviewUrl(URL.createObjectURL(file));
    setStep('preview');
  };

  const handleUpload = async () => {
    if (!selectedFile) return;
    setStep('uploading');

    const docMeta = ALL_DOC_TYPES.find(d => d.value === currentDocType);
    const docEntry = {
      type: currentDocType,
      label: docMeta?.label || currentDocType,
      url: previewUrl,
      uploaded_at: new Date().toISOString(),
    };

    try {
      const formData = new FormData();
      formData.append('document', selectedFile);
      formData.append('document_type', currentDocType);
      const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
      const token = localStorage.getItem('driver_app_token');

      const res = await fetch(`${BACKEND_URL}/api/driver-mobile/documents/scan`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: formData,
      });

      if (res.ok) {
        const data = await res.json();
        if (data.user) updateUser(data.user);
        if (data.document_url) docEntry.url = data.document_url;
      }
    } catch {
      // Store locally if backend unavailable
    }

    // Save locally
    const newDocs = [...uploadedDocs, docEntry];
    setUploadedDocs(newDocs);
    if (user?.id) {
      localStorage.setItem(`driver_docs_${user.id}`, JSON.stringify(newDocs));
    }

    setSelectedFile(null);
    setPreviewUrl(null);
    setStep('list');
  };

  const handleFinish = () => {
    markProfileComplete();
    onComplete();
  };

  // ── Uploading ─────────────────────────────────────────────────────────────
  if (step === 'uploading') {
    return (
      <div className={`min-h-screen flex flex-col items-center justify-center p-6 font-['Oxanium'] ${bg}`}>
        <div className="w-16 h-16 border-4 border-red-600 border-t-transparent rounded-full animate-spin mb-6" />
        <h1 className={`text-xl font-bold ${text}`}>UPLOADING...</h1>
        <p className={`text-sm mt-2 ${subtext}`}>Saving your document securely</p>
      </div>
    );
  }

  // ── Preview ───────────────────────────────────────────────────────────────
  if (step === 'preview') {
    const docMeta = ALL_DOC_TYPES.find(d => d.value === currentDocType);
    return (
      <div className={`min-h-screen flex flex-col font-['Oxanium'] ${bg}`}>
        <div className={`pt-12 pb-6 px-6 border-b ${border}`}>
          <h1 className={`text-xl font-bold ${text}`}>REVIEW DOCUMENT</h1>
          <p className={`mt-1 text-sm ${subtext}`}>Confirm this is a clear, readable photo</p>
        </div>
        <div className="flex-1 px-6 py-6 space-y-4">
          {previewUrl && (
            <img src={previewUrl} alt="Captured document"
              className={`w-full border ${border} object-contain max-h-64 rounded`} />
          )}
          <p className={`text-xs ${subtext}`}>
            Type: <span className={`font-medium ${text}`}>{docMeta?.label}</span>
          </p>
          <div className="space-y-3 pt-2">
            <button onClick={handleUpload}
              className="w-full bg-red-600 hover:bg-red-700 text-white font-semibold py-4 tracking-wider">
              CONFIRM &amp; UPLOAD
            </button>
            <button onClick={() => { setStep('scan'); setSelectedFile(null); setPreviewUrl(null); }}
              className={`w-full border font-semibold py-3 tracking-wider ${border} ${text}`}>
              RETAKE
            </button>
          </div>
        </div>
      </div>
    );
  }

  // ── Camera/file capture ───────────────────────────────────────────────────
  if (step === 'scan') {
    const docMeta = ALL_DOC_TYPES.find(d => d.value === currentDocType);
    return (
      <div className={`min-h-screen flex flex-col font-['Oxanium'] ${bg}`}>
        <div className={`pt-12 pb-6 px-6 border-b ${border}`}>
          <button onClick={() => setStep('list')} className={`text-sm ${subtext} mb-3 flex items-center gap-1`}>
            ← Back
          </button>
          <h1 className={`text-xl font-bold ${text}`}>SCAN {docMeta?.label?.toUpperCase()}</h1>
          <p className={`mt-1 text-sm ${subtext}`}>Place the document on a flat surface with good lighting</p>
        </div>
        <div className="flex-1 px-6 py-6 space-y-4">
          <div className={`border ${border} ${card} p-6 text-center`}>
            <p className="text-4xl mb-3">{docMeta?.icon}</p>
            <p className={`text-sm ${subtext}`}>Take a photo or upload from your gallery</p>
          </div>
          <input ref={fileInputRef} type="file" accept="image/*" capture="environment"
            onChange={handleFileSelect} className="hidden" />
          <button onClick={() => fileInputRef.current?.click()}
            className="w-full bg-red-600 hover:bg-red-700 text-white font-semibold py-4 tracking-wider flex items-center justify-center gap-2">
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" />
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 13a3 3 0 11-6 0 3 3 0 016 0z" />
            </svg>
            OPEN CAMERA / GALLERY
          </button>
        </div>
      </div>
    );
  }

  // ── Document checklist (main view) ────────────────────────────────────────
  return (
    <div className={`min-h-screen flex flex-col font-['Oxanium'] ${bg}`}>
      {/* Header */}
      <div className={`pt-12 pb-6 px-6 border-b ${border}`}>
        <div className="w-14 h-14 bg-red-600 flex items-center justify-center mb-4">
          <svg className="w-7 h-7 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
              d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
          </svg>
        </div>
        <h1 className={`text-2xl font-bold tracking-wider ${text}`}>
          {hasRequiredDocs ? 'REQUIRED DOCUMENTS' : 'COMPLETE YOUR PROFILE'}
        </h1>
        <p className={`mt-1 text-sm ${subtext}`}>
          {hasRequiredDocs
            ? 'Your dispatcher requires these documents before you can start'
            : 'Upload your license and certifications to get started'}
        </p>

        {/* Progress bar */}
        <div className="mt-4">
          <div className="flex justify-between text-xs mb-1">
            <span className={subtext}>{requiredUploaded} of {totalRequired} uploaded</span>
            {allDone && <span className="text-green-500 font-medium">All done ✓</span>}
          </div>
          <div className={`w-full h-1.5 rounded-full ${isDark ? 'bg-[#262626]' : 'bg-[#e5e5e5]'}`}>
            <div
              className="h-1.5 rounded-full bg-red-600 transition-all duration-500"
              style={{ width: totalRequired > 0 ? `${(requiredUploaded / totalRequired) * 100}%` : '0%' }}
            />
          </div>
        </div>
      </div>

      {/* Document list */}
      <div className="flex-1 px-6 py-5 space-y-3 overflow-y-auto">
        {docList.map(doc => {
          const uploaded = uploadedTypes.includes(doc.value);
          const isRequired = hasRequiredDocs ? requiredDocuments.includes(doc.value) : true;
          return (
            <button
              key={doc.value}
              onClick={() => !uploaded && startScan(doc.value)}
              disabled={uploaded}
              className={`w-full flex items-center gap-4 p-4 border text-left transition-colors ${
                uploaded
                  ? `border-green-600/40 ${isDark ? 'bg-green-950/20' : 'bg-green-50'}`
                  : `${border} ${card} hover:border-red-600/50 active:border-red-600`
              }`}
            >
              <div className={`w-10 h-10 flex items-center justify-center text-xl flex-shrink-0 ${
                uploaded ? (isDark ? 'bg-green-950/40' : 'bg-green-100') : (isDark ? 'bg-[#171717]' : 'bg-white')
              }`}>
                {uploaded ? '✅' : doc.icon}
              </div>
              <div className="flex-1 min-w-0">
                <p className={`font-medium text-sm ${uploaded ? 'text-green-500' : text}`}>
                  {doc.label}
                </p>
                <p className={`text-xs mt-0.5 ${subtext}`}>
                  {uploaded
                    ? `Uploaded ${new Date(uploadedDocs.find(u => u.type === doc.value)?.uploaded_at || '').toLocaleDateString()}`
                    : isRequired ? 'Required — tap to upload' : 'Tap to upload'}
                </p>
              </div>
              <div className="flex-shrink-0">
                {uploaded ? (
                  <div className="w-6 h-6 bg-green-600 rounded-full flex items-center justify-center">
                    <svg className="w-3 h-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                    </svg>
                  </div>
                ) : (
                  <div className={`w-6 h-6 border-2 rounded-full flex items-center justify-center ${
                    isDark ? 'border-white/20' : 'border-black/20'
                  }`}>
                    <svg className={`w-3 h-3 ${subtext}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                  </div>
                )}
              </div>
            </button>
          );
        })}

        {/* Add extra document */}
        {!hasRequiredDocs && (
          <button
            onClick={() => startScan('other')}
            className={`w-full flex items-center gap-4 p-4 border border-dashed text-left ${
              isDark ? 'border-[#333] text-white/30 hover:border-white/20' : 'border-[#ccc] text-black/30 hover:border-black/30'
            }`}
          >
            <div className="text-xl">➕</div>
            <p className="text-sm">Add another document</p>
          </button>
        )}
      </div>

      {/* Footer actions */}
      <div className={`px-6 py-5 border-t ${border} space-y-3`}>
        {allDone ? (
          <button onClick={handleFinish}
            className="w-full bg-green-600 hover:bg-green-700 text-white font-semibold py-4 tracking-wider">
            ALL DOCUMENTS UPLOADED — CONTINUE
          </button>
        ) : (
          <>
            {requiredUploaded > 0 && (
              <button onClick={handleFinish}
                className="w-full bg-red-600 hover:bg-red-700 text-white font-semibold py-4 tracking-wider">
                CONTINUE ({requiredUploaded}/{totalRequired} DONE)
              </button>
            )}
            <button onClick={handleFinish}
              className={`w-full border py-3 text-sm tracking-wider ${border} ${subtext}`}>
              {requiredUploaded > 0 ? 'FINISH LATER' : 'SKIP FOR NOW'}
            </button>
          </>
        )}
      </div>
    </div>
  );
};

export default LicenseScannerScreen;
